// Moteur audio (Web Audio API) — refonte qualité.
// Objectifs : démarrage/arrêt sans claquement, boucles inaudibles, niveaux
// perçus cohérents entre ambiances, un seul lecteur actif à la fois.
// Origine : sons entièrement synthétiques (aucun fichier tiers dans ce build).

let ctx = null;
let masterGain = null;
let activeNodes = [];
let activeMediaEl = null;
let currentAmbient = null;
let currentVolume = 0.6;

const RECORDED_PATHS = {
  rain: "/audio/rain.mp3",
  ocean: "/audio/ocean.mp3",
};

// Gain relatif par ambiance pour équilibrer la sensation d'intensité perçue
const GAIN_TRIM = {
  pink: 0.55,
  brown: 0.90,
  rain: 0.75,
  ocean: 0.85,
  forest: 0.70,
};

export async function isRecordedAvailable(id) {
  const path = RECORDED_PATHS[id];
  if (!path) return false;
  try {
    const r = await fetch(path, { method: "HEAD" });
    if (!r.ok) return false;
    const ct = (r.headers.get("content-type") || "").toLowerCase();
    return ct.includes("audio") || ct.includes("mpeg") || ct.includes("mp3") || ct.includes("ogg") || ct.includes("wav");
  } catch { return false; }
}

function getCtx() {
  if (!ctx) {
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return null;
      ctx = new AC();
      masterGain = ctx.createGain();
      masterGain.gain.value = 0;
      masterGain.connect(ctx.destination);
    } catch (e) { return null; }
  }
  return ctx;
}

export function isAudioSupported() {
  return !!(window.AudioContext || window.webkitAudioContext);
}

async function ensureRunning() {
  const c = getCtx();
  if (!c) return false;
  if (c.state === "suspended") {
    try { await c.resume(); } catch (e) { /* ignore */ }
  }
  return c.state === "running";
}

// --- Buffers cachés pour éviter la boucle audible ---
const bufferCache = new Map();

function noiseBuffer(type, seconds) {
  const key = `${type}:${seconds}`;
  if (bufferCache.has(key)) return bufferCache.get(key);
  const c = getCtx();
  const length = Math.floor(c.sampleRate * seconds);
  const buf = c.createBuffer(2, length, c.sampleRate);
  for (let ch = 0; ch < 2; ch++) {
    const data = buf.getChannelData(ch);
    if (type === "white") {
      for (let i = 0; i < length; i++) data[i] = Math.random() * 2 - 1;
    } else if (type === "pink") {
      // Filtre de Paul Kellet — bruit rose de qualité
      let b0 = 0, b1 = 0, b2 = 0, b3 = 0, b4 = 0, b5 = 0, b6 = 0;
      for (let i = 0; i < length; i++) {
        const w = Math.random() * 2 - 1;
        b0 = 0.99886 * b0 + w * 0.0555179;
        b1 = 0.99332 * b1 + w * 0.0750759;
        b2 = 0.96900 * b2 + w * 0.1538520;
        b3 = 0.86650 * b3 + w * 0.3104856;
        b4 = 0.55000 * b4 + w * 0.5329522;
        b5 = -0.7616 * b5 - w * 0.0168980;
        data[i] = (b0 + b1 + b2 + b3 + b4 + b5 + b6 + w * 0.5362) * 0.11;
        b6 = w * 0.115926;
      }
    } else if (type === "brown") {
      let last = 0;
      for (let i = 0; i < length; i++) {
        const w = Math.random() * 2 - 1;
        last = (last + 0.02 * w) / 1.02;
        data[i] = last * 3.5;
      }
    }
    // Crossfade des 200 dernières ms avec le début pour éliminer les clicks de boucle
    const fade = Math.floor(c.sampleRate * 0.2);
    for (let i = 0; i < fade; i++) {
      const g = i / fade;
      const j = length - fade + i;
      data[j] = data[j] * (1 - g) + data[i] * g;
    }
  }
  bufferCache.set(key, buf);
  return buf;
}

function loopSource(type, seconds = 8) {
  const c = getCtx();
  const src = c.createBufferSource();
  src.buffer = noiseBuffer(type, seconds);
  src.loop = true;
  return src;
}

// --- Modulateur LFO doux pour éviter la sensation statique ---
function slowLfo(freqHz, depth) {
  const c = getCtx();
  const osc = c.createOscillator();
  osc.frequency.value = freqHz;
  osc.type = "sine";
  const g = c.createGain();
  g.gain.value = depth;
  osc.connect(g);
  osc.start();
  return { osc, g };
}

function buildAmbient(id) {
  const c = getCtx();
  const out = c.createGain();
  out.gain.value = GAIN_TRIM[id] || 0.7;

  if (id === "pink") {
    const src = loopSource("pink", 8);
    const lp = c.createBiquadFilter();
    lp.type = "lowpass"; lp.frequency.value = 4000; lp.Q.value = 0.5;
    const hp = c.createBiquadFilter();
    hp.type = "highpass"; hp.frequency.value = 60; hp.Q.value = 0.5;
    src.connect(hp).connect(lp).connect(out);
    // Modulation très lente du filtre pour "respirer"
    const lfo = slowLfo(0.05, 400);
    lfo.g.connect(lp.frequency);
    src.start();
    activeNodes.push(src, lp, hp, lfo.osc, lfo.g, out);
  } else if (id === "brown") {
    const src = loopSource("brown", 8);
    const lp = c.createBiquadFilter();
    lp.type = "lowpass"; lp.frequency.value = 350; lp.Q.value = 0.6;
    src.connect(lp).connect(out);
    src.start();
    activeNodes.push(src, lp, out);
  } else if (id === "rain") {
    // Pluie douce : bruit rose filtré passe-bande large + très légère modulation
    // Aucun tick d'oscillateur (source de clics), tout est continu.
    const src = loopSource("pink", 10);
    const hp = c.createBiquadFilter();
    hp.type = "highpass"; hp.frequency.value = 700; hp.Q.value = 0.4;
    const lp = c.createBiquadFilter();
    lp.type = "lowpass"; lp.frequency.value = 5200; lp.Q.value = 0.5;
    const peak = c.createBiquadFilter();
    peak.type = "peaking"; peak.frequency.value = 2200; peak.Q.value = 0.7; peak.gain.value = 3;
    src.connect(hp).connect(lp).connect(peak).connect(out);
    // Micro-mouvement du passe-bas pour éviter la sensation statique
    const lfo = slowLfo(0.07, 700);
    lfo.g.connect(lp.frequency);
    src.start();
    activeNodes.push(src, hp, lp, peak, lfo.osc, lfo.g, out);
  } else if (id === "ocean") {
    // Océan : bruit brun avec vagues lentes via LFO d'amplitude sinusoïdal + filtre modulé
    const src = loopSource("brown", 10);
    const lp = c.createBiquadFilter();
    lp.type = "lowpass"; lp.frequency.value = 900; lp.Q.value = 0.4;
    const wave = c.createGain(); wave.gain.value = 0.45;
    src.connect(lp).connect(wave).connect(out);
    // Amplitude en vagues (~8s)
    const lfoAmp = slowLfo(0.12, 0.35);
    lfoAmp.g.connect(wave.gain);
    // Ouverture/fermeture du passe-bas plus rapide pour l'écume
    const lfoFilt = slowLfo(0.18, 250);
    lfoFilt.g.connect(lp.frequency);
    src.start();
    activeNodes.push(src, lp, wave, lfoAmp.osc, lfoAmp.g, lfoFilt.osc, lfoFilt.g, out);
  } else if (id === "forest") {
    // Ambiance forestière : bruit rose très filtré + subtile modulation
    const src = loopSource("pink", 10);
    const hp = c.createBiquadFilter();
    hp.type = "highpass"; hp.frequency.value = 300; hp.Q.value = 0.4;
    const lp = c.createBiquadFilter();
    lp.type = "lowpass"; lp.frequency.value = 2600; lp.Q.value = 0.4;
    src.connect(hp).connect(lp).connect(out);
    const lfo = slowLfo(0.06, 500);
    lfo.g.connect(lp.frequency);
    src.start();
    activeNodes.push(src, hp, lp, lfo.osc, lfo.g, out);
  } else {
    return null;
  }

  out.connect(masterGain);
  return out;
}

// Fondu global au démarrage/arrêt : démarrage sans claquement (2,5 s) puis fondu de sortie (1,5 - 2 s)
const ATTACK_S = 2.5;
const RELEASE_S = 2.0;

export async function playAmbient(id, volume = currentVolume, { preferRecorded = false } = {}) {
  const ok = await ensureRunning();
  if (!ok) return { ok: false, error: "audio-unavailable" };
  stopAmbient({ fade: false });

  if (preferRecorded && RECORDED_PATHS[id]) {
    const available = await isRecordedAvailable(id);
    if (available) {
      try {
        const el = new Audio(RECORDED_PATHS[id]);
        el.loop = true;
        el.crossOrigin = "anonymous";
        const source = ctx.createMediaElementSource(el);
        source.connect(masterGain);
        await el.play();
        activeMediaEl = el;
        activeNodes.push(source);
        currentAmbient = id;
        currentVolume = volume;
        const now = ctx.currentTime;
        masterGain.gain.cancelScheduledValues(now);
        masterGain.gain.setValueAtTime(0, now);
        masterGain.gain.linearRampToValueAtTime(volume, now + ATTACK_S);
        return { ok: true, mode: "recorded" };
      } catch (e) { /* fallback synthé */ }
    } else {
      return { ok: false, error: "recorded-missing" };
    }
  }

  const node = buildAmbient(id);
  if (!node) return { ok: false, error: "ambient-not-found" };
  currentAmbient = id;
  currentVolume = volume;
  const now = ctx.currentTime;
  masterGain.gain.cancelScheduledValues(now);
  masterGain.gain.setValueAtTime(0, now);
  masterGain.gain.linearRampToValueAtTime(volume, now + ATTACK_S);
  return { ok: true, mode: "synthetic" };
}

export function setVolume(v) {
  currentVolume = Math.max(0, Math.min(1, v));
  if (!ctx || !masterGain) return;
  const now = ctx.currentTime;
  masterGain.gain.cancelScheduledValues(now);
  masterGain.gain.linearRampToValueAtTime(currentVolume, now + 0.2);
}

export function getVolume() { return currentVolume; }

export function stopAmbient({ fade = true, fadeSeconds = RELEASE_S } = {}) {
  if (!ctx || !masterGain) return;
  const now = ctx.currentTime;
  if (fade) {
    masterGain.gain.cancelScheduledValues(now);
    masterGain.gain.setValueAtTime(masterGain.gain.value, now);
    masterGain.gain.linearRampToValueAtTime(0.0001, now + fadeSeconds);
    setTimeout(() => cleanup(), fadeSeconds * 1000 + 80);
  } else {
    masterGain.gain.cancelScheduledValues(now);
    masterGain.gain.setValueAtTime(0, now);
    cleanup();
  }
}

function cleanup() {
  activeNodes.forEach((n) => {
    try {
      if (n && n._interval) { clearInterval(n._interval); return; }
      if (n && n.stop) n.stop();
      if (n && n.disconnect) n.disconnect();
    } catch (e) { /* ignore */ }
  });
  activeNodes = [];
  if (activeMediaEl) {
    try { activeMediaEl.pause(); activeMediaEl.src = ""; } catch (e) { /* ignore */ }
    activeMediaEl = null;
  }
  currentAmbient = null;
}

export function getCurrentAmbient() { return currentAmbient; }
export function isPlaying() { return !!currentAmbient && !!ctx && ctx.state === "running"; }

// --- Aperçu court d'une ambiance (~6 s), auto-arrêt ---
let previewTimer = null;
export async function previewAmbient(id, seconds = 6) {
  if (previewTimer) { clearTimeout(previewTimer); previewTimer = null; }
  const res = await playAmbient(id, 0.5);
  if (!res.ok) return res;
  previewTimer = setTimeout(() => {
    stopAmbient({ fade: true, fadeSeconds: 1.0 });
    previewTimer = null;
  }, seconds * 1000);
  return res;
}
